import { useStore } from '../../store/store';
// Common
import './Utils.css';
import NavbarSelector from '../Layout/NavbarSelector';
import SectionTreeSelector from '../Layout/SectionTreeSelector';
import DuotoneLightIcon from './DuotoneLightIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { pageHeight, pageWidth } from '../../constants/gridConfig';
import { ItemTypes, PRIMARY_DARK, VARIABLE_COLOR, getPrimaryDark, viewerTypes } from '../../constants/constants';
import { useEffect, useMemo, useState } from 'react';
import { useHandleBoxSelect, useQueryDocumentSections } from '../../hooks';
import { initializeBoxContent, isAVariableBox } from '../../utils/boxes';
import { leftShownStatus, useOverlay } from '../../contexts/OverlayContext';
import { Spin, Radio, Modal, Button, message, Switch, Segmented, Upload } from 'antd';
import RecursiveModelConfigModal from '../RightSide/RecursiveModelConfigModal';
import { LoadingOutlined } from '@ant-design/icons';
import { postCostingDocumentPdf, postPdfInTemplate } from '../../requests/templates';
import VariableSwitcher from './VariableSwitcher';
import { getSelectedSection } from '../../utils/sections';
import {
  buildRecursiveNestMeta,
  deriveAgreementType,
  isNestableRecursiveChild,
  shouldApplyRecursiveNestFilter,
} from '../../utils/recursiveSections';
import HideInFormSwitcher from './HideInFormSwitcher';
import BoxButton from './BoxButton';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
import CostingDocumentPicker from './CostingDocumentPicker';
import SectionPreviewModal from './SectionPreviewModal';
import { getContainerByColumnId } from '../../store/lookups';
const iconFontSize = 25

// Bloc affiché dans la box tant qu'elle attend son contenu (section à choisir,
// PDF à importer). Les contrôles, eux, vivent dans l'inspecteur de droite :
// posés en travers du document ils en cassaient la lecture, et leur largeur
// dictait celle de la box. Même facture que le placeholder RECURSIVE_MODEL.
// `action` : bouton d'édition. Il porte le geste à faire, plutôt que de le
// décrire dans le sous-titre — un utilisateur qui cherche où cliquer balaye les
// boutons, pas les lignes grises.
const PlaceholderCard = ({ iconName, title, hint, action }) => (
  <div style={{
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 20px',
    boxSizing: 'border-box',
    minWidth: 0,
  }}>
    <DuotoneLightIcon name={iconName} style={{ fontSize: 18, color: '#1d1d1d', flexShrink: 0 }} />
    <div style={{ minWidth: 0 }}>
      <div style={{
        color: '#1d1d1d',
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {title}
      </div>
      <div style={{ color: '#434343', fontSize: 11, lineHeight: 1.4, marginTop: 2 }}>{hint}</div>
    </div>
    {action && <div style={{ marginLeft: 'auto', flexShrink: 0 }}>{action}</div>}
  </div>
)

const selector =
  (id) =>
    ({
      boxes,
      masks,
      injectMask,
      injectSection,
      landscape,
      currentDocument,
      currentProposal,
      updateBox,
      handleSectionAction,
      setLoaderPdfToImage,
      setTempContainerPdfToImage,
      selectedContainer,
      handleUnSelectBox,
      handleUnSelectContainer,
      sections,
      folders,
      splittableTables,
      currentTemplate,
      recursiveSectionRelationships,
      recursiveModelLabels,
      setRecursiveSectionChild,
      containers,
    }) => {
      const box = boxes.find((b) => b.id === id);
      const maskList = masks.filter(({ id }) => !["header", "footer"].includes(id))
      let inject;
      let selectedValue;
      if (box.type === ItemTypes.MASK) {
        inject = injectMask
      } else if (box.type === ItemTypes.SECTION_VARIABLE) {
        if (currentDocument.type === 'template') {
          inject = ({ id: optionId, boxId: id }) => updateBox(id, (box) => {
            box.answer = optionId
          })
        } else {
          inject = ({ id: optionId, boxId }) => handleSectionAction({
            optionId,
            boxId,
            sectionId: box.sectionId,
            action: box.questions.find((q) => q.id === optionId).label === 'Oui' ? "inject" : "remove"
          })
        }
        selectedValue = box.answer
      } else if (box.type === ItemTypes.RECURSIVE_SECTION) {
        inject = ({ id: childSectionId, boxId }) => {
          const childSection = sections?.find((s) => s.id === childSectionId);
          if (!childSection) return;

          const parentModel = currentTemplate?.recursiveModelName;
          const childModel = childSection.recursiveModelName;
          const agreementType = deriveAgreementType(currentTemplate);

          if (!parentModel || !childModel) {
            message.warning("Impossible d'imbriquer : modèle parent ou enfant manquant.");
            return;
          }
          const allowed = recursiveSectionRelationships?.[agreementType]?.[parentModel];
          if (!allowed?.includes(childModel)) {
            message.warning(
              `Aucune relation connue entre [${parentModel}] et [${childModel}] pour la convention [${agreementType}]. Impossible d'imbriquer.`
            );
            return;
          }
          setRecursiveSectionChild(boxId, childSection);
        };
        selectedValue = box.content?.childTemplateId ?? undefined;
      } else {
        inject = injectSection
      }
      return {
        maskList,
        box,
        inject,
        landscape,
        setLoaderPdfToImage,
        setTempContainerPdfToImage,
        updateBox,
        selectedContainer,
        // Container qui porte la box, indépendamment de la sélection courante :
        // l'import PDF cible ce container-là, et le déclencher depuis
        // l'inspecteur ne passe plus forcément par un clic sur le container.
        boxContainer: getContainerByColumnId(containers, box.columnId),
        documentType: currentDocument.type,
        documentId: currentDocument.type === 'template' ? currentDocument.id : currentProposal.id,
        currentDocument,
        selectedValue,
        currentProposal,
        handleUnSelectBox,
        handleUnSelectContainer,
        sections,
        folders,
        splittableTables,
        currentTemplate,
        recursiveSectionRelationships,
        recursiveModelLabels,
      }
    };

// 4e choix du groupe « Mode d'insertion » : ce n'est pas un mode stocké dans
// sectionMode (1/2/3) mais la conversion de la box en question de section.
const SECTION_MODE_VARIABLE = 4;

const SelectionBox = ({ id, fromViewer }) => {
  const {
    inject,
    box,
    maskList,
    landscape,
    documentType,
    documentId,
    selectedValue,
    setLoaderPdfToImage,
    setTempContainerPdfToImage,
    updateBox,
    selectedContainer,
    boxContainer,
    currentDocument,
    handleUnSelectBox,
    handleUnSelectContainer,
    sections,
    folders,
    splittableTables,
    currentTemplate,
    recursiveSectionRelationships,
    recursiveModelLabels,
  } = useStore(selector(id));

  const [pendingSplitChoice, setPendingSplitChoice] = useState(null);
  const [isApplyingSplit, setIsApplyingSplit] = useState(false);
  const [nestableOnly, setNestableOnly] = useState(false);
  // Proposal-side toggle between standard and recursive sections (in a template this is
  // driven by the "Récursive" radio / sectionMode instead).
  const [showRecursive, setShowRecursive] = useState(false);
  const [recursiveModelConfigOpen, setRecursiveModelConfigOpen] = useState(false);
  // Section dont on regarde l'aperçu (œil d'une option du sélecteur). La modale
  // est montée une seule fois, hors du menu déroulant : celui-ci se ferme au
  // clic, il ne peut pas héberger la fenêtre.
  const [previewSectionId, setPreviewSectionId] = useState(null);
  // Ouverture du menu pilotée ici : l'aperçu se regarde SANS perdre sa place.
  // Le menu reste ouvert derrière la modale, et fermer celle-ci ramène la liste
  // telle quelle — on compare deux sections sans rouvrir le sélecteur ni
  // resélectionner la box à chaque fois.
  const [selectorOpen, setSelectorOpen] = useState(false);
  // Focus de la recherche du sélecteur, une valeur par demande (cf. focusNonce).
  const [selectorFocusNonce, setSelectorFocusNonce] = useState(null);
  // Repère sur la section du panel où se choisit le contenu, demandé par le
  // bouton de l'emplacement : une valeur par demande (cf. BoxFormControlsPortal).
  const [panelFlash, setPanelFlash] = useState(null);
  const endPanelFlash = () => setPanelFlash(null);
  const handleSelectorOpenChange = (open) => {
    // Le clic dans la modale (et son masque) est un clic « hors du menu » pour
    // antd : sans ce garde-fou, ouvrir l'aperçu referme la liste.
    if (!open && previewSectionId != null) return;
    setSelectorOpen(open);
  };
  // 1=Liée, 2=Édition, 3=Récursive. `box.sectionMode` n'est posé que par le
  // retour depuis « Variable », pour retrouver le mode qu'on vient d'y choisir.
  const [sectionMode, setSectionMode] = useState(box.sectionMode ?? 1);
  const { data: sectionList, isLoading } = useQueryDocumentSections({ documentType, documentId })
  const { t } = useTranslation();
  const { updateLeftShownStatus, overlayProps, handleOverlayCollapse } = useOverlay();
  const shownStatus = overlayProps?.left?.shownStatus
  // Mise en page du PDF importé, choisie dans l'inspecteur et enregistrée sur
  // la box : le formulaire, qui n'affiche pas ce choix, importe avec celui du
  // modèle. En paysage, l'import est obligatoirement en pleine page.
  const drawPdfToImage = !landscape && (box.drawPdfToImage ?? true)
  const setDrawPdfToImage = (value) => updateBox(id, (draft) => {
    draft.drawPdfToImage = value
  })
  const linkedSection = sectionMode === 1 || sectionMode === 3;
  const isSelected = useStore(({ selectedBoxId }) => selectedBoxId === id)
  // fromViewer transmis comme dans Box.jsx : la sélection commande désormais
  // l'inspecteur, on ne la déclenche pas depuis un aperçu (aperçu, dmz, numéro
  // de page) où l'on ne compose pas le document.
  const handleBoxSelect = useHandleBoxSelect({ fromViewer, box, isSelected })
  const dropBoxInColumn = useStore(({ dropBoxInColumn }) => dropBoxInColumn)
  const selectBox = useStore(({ handleSelectBox }) => handleSelectBox)
  const gridConfiguration = useStore(({ gridConfiguration }) => gridConfiguration)
  const configuration = useStore(({ configuration }) => configuration)
  const column = useStore(({ columns }) => columns.find(({ id }) => id === box.columnId))
  const accentColor = getPrimaryDark(documentType === 'template')

  // Dans le formulaire de variables, la box se remplit sur place : c'est là que
  // l'utilisateur répond, l'inspecteur de droite n'est pas son terrain.
  const inFormViewer = fromViewer === viewerTypes.VARIABLES
  const isPdfBox = box.type === ItemTypes.FILE_PDF || box.type === ItemTypes.FILE_PDF_VARIABLE
  // Boxes en attente d'un choix de section (section, section récursive, masque).
  const isSectionSelector = ![
    ItemTypes.SECTION_VARIABLE,
    ItemTypes.FILE_PDF,
    ItemTypes.FILE_PDF_VARIABLE,
    ItemTypes.SECTION_LINK,
    ItemTypes.RECURSIVE_MODEL,
  ].includes(box.type)
  // Formulaire ouvert : la box du canvas ET celle du formulaire sont montées et
  // toutes deux « sélectionnées ». Une seule alimente l'inspecteur, sinon ses
  // sections y apparaîtraient en double.
  const showBoxPanel = isSelected && (inFormViewer
    ? shownStatus === 'variables'
    : shownStatus !== 'variables')

  // Box non sélectionnée (clic ailleurs, ou sélection refusée) : le repère
  // n'a plus de section où se jouer. L'oublier évite qu'il ne ressurgisse à la
  // prochaine sélection par simple clic.
  useEffect(() => {
    if (!isSelected && panelFlash != null) setPanelFlash(null);
  }, [isSelected, panelFlash]);

  const selectionList = useMemo(() => {
    let list = []
    let recursiveMeta = null
    if (box.type === ItemTypes.MASK) {
      list = maskList
    } else if (box.type === ItemTypes.SECTION_VARIABLE) {
      list = box.questions.map((q) => ({ ...q, name: q.label }))
    } else if (box.type === ItemTypes.RECURSIVE_SECTION) {
      // Only recursive sections are child candidates. Valid ones (per the
      // collective-agreement relationships) are selectable; the rest are shown
      // disabled with a reason so the user understands what can be nested.
      // Exception: opportunity parent allows any non-opportunity child.
      const parentModel = currentTemplate?.recursiveModelName;
      recursiveMeta = buildRecursiveNestMeta(
        recursiveSectionRelationships,
        currentTemplate,
        parentModel
      );
      list = (sectionList || []).filter(
        (s) => s.isRecursive && s.recursiveModelName && s.id !== currentDocument?.id
      );
    } else {
      list = (sectionList || []).filter(({ id }) => id !== (currentDocument.type === 'template'
        ? currentDocument.id
        : true));
      // Template: "Récursive" radio (sectionMode 3). Proposal: dedicated toggle (showRecursive).
      const wantRecursive = currentDocument.type === 'template' ? sectionMode === 3 : showRecursive;
      if (wantRecursive) {
        list = list.filter((s) => s.isRecursive);
        // Nesting constraints only apply when authoring a (recursive) template; a proposal
        // injects a top-level recursive section directly, so no parent/child filtering.
        if (currentDocument.type === 'template') {
          if (shouldApplyRecursiveNestFilter(currentTemplate)) {
            const parentModel = currentTemplate.recursiveModelName;
            recursiveMeta = buildRecursiveNestMeta(
              recursiveSectionRelationships,
              currentTemplate,
              parentModel
            );
            list = list.filter(
              (s) =>
                s.recursiveModelName &&
                s.recursiveModelName !== parentModel &&
                isNestableRecursiveChild(recursiveMeta, s.recursiveModelName)
            );
          } else if (currentTemplate?.isRecursive && currentTemplate?.recursiveModelName) {
            list = list.filter(
              (s) => s.recursiveModelName !== currentTemplate.recursiveModelName
            );
          }
        }
      } else {
        list = list.filter((s) => !s.isRecursive);
      }
    }
    const recursiveIcon = <FontAwesomeIcon icon={icon({ name: 'arrow-turn-down-right', style: 'regular' })} />;
    // Œil d'aperçu : réservé aux listes de sections. Les masques et les réponses
    // d'une question de section ne désignent pas une section à prévisualiser.
    const withPreview = box.type !== ItemTypes.MASK && box.type !== ItemTypes.SECTION_VARIABLE;
    const previewEye = (sectionId) => (
      <span
        role='button'
        tabIndex={-1}
        title={t('section_preview_open')}
        // Sans ces deux arrêts, cliquer l'œil vaut choix de l'option : rc-select
        // sélectionne au mousedown, et la section serait injectée au lieu d'être
        // montrée.
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
          setPreviewSectionId(sectionId);
        }}
        style={{ padding: '0 4px', cursor: 'pointer', color: 'rgba(11, 34, 57, 0.45)' }}
      >
        <FontAwesomeIcon icon={icon({ name: 'eye', style: 'light' })} />
      </span>
    );
    return list.map((item) => {
      const base = {
        value: item.id,
        label: item.name,
        folder_id: item.folder_id ?? null,
        ...(withPreview ? { extra: previewEye(item.id) } : {}),
      };
      if (recursiveMeta) {
        const ok = recursiveMeta.allowed === null
          ? item.recursiveModelName !== recursiveMeta.parentModel
          : recursiveMeta.allowed.has(item.recursiveModelName);
        const parentLabel = recursiveModelLabels?.[recursiveMeta.parentModel] ?? recursiveMeta.parentModel;
        return {
          ...base,
          disabled: !ok,
          title: ok ? undefined : `Non imbricable sous « ${parentLabel} »`,
          icon: recursiveIcon,
        };
      }
      // Differentiate recursive sections in any section dropdown.
      if (item.isRecursive) {
        const modelLabel = recursiveModelLabels?.[item.recursiveModelName] ?? item.recursiveModelName;
        const workLabel = item.work ? t(item.work) : null;
        const suffix = [modelLabel, workLabel].filter(Boolean).join(' - ');
        return { ...base, label: suffix ? `${item.name}  (${suffix})` : item.name, icon: recursiveIcon };
      }
      return base;
    });
  }, [box.type, box.questions, maskList, sectionList, currentDocument, currentTemplate, recursiveSectionRelationships, recursiveModelLabels, sectionMode, showRecursive]);
  const selectedSectionVariable = useMemo(() => {
    if (box.type === ItemTypes.SECTION_VARIABLE) return getSelectedSection(box.sectionId, sectionList)
    return null
  }, [box?.sectionId, box.type, sectionList])


  const handleSelect = (optionId) => {
    if (box.type === ItemTypes.RECURSIVE_SECTION) {
      // inject handles validation internally
      inject({ id: optionId, boxId: id });
      handleUnSelectBox();
      handleUnSelectContainer();
      return;
    }
    if (box.type !== ItemTypes.SECTION_VARIABLE && box.type !== ItemTypes.MASK) {
      const selectedSection = (sectionList || sections)?.find((s) => s.id === optionId);
      if (
        sectionMode === 3 &&
        shouldApplyRecursiveNestFilter(currentTemplate) &&
        selectedSection?.recursiveModelName
      ) {
        const nestMeta = buildRecursiveNestMeta(
          recursiveSectionRelationships,
          currentTemplate,
          currentTemplate.recursiveModelName
        );
        if (!isNestableRecursiveChild(nestMeta, selectedSection.recursiveModelName)) {
          const parentLabel =
            recursiveModelLabels?.[currentTemplate.recursiveModelName] ??
            currentTemplate.recursiveModelName;
          message.warning(
            `Cette section n'est pas imbricable sous « ${parentLabel} ».`
          );
          return;
        }
      }
      if (
        selectedSection?.isRecursive &&
        selectedSection?.recursiveModelName &&
        currentDocument.type !== 'template'
      ) {
        inject({ id: optionId, boxId: id, linked_section: false, split: false });
        handleUnSelectBox();
        handleUnSelectContainer();
        return;
      }
    }
    inject({ id: optionId, boxId: id, linked_section: box.type !== ItemTypes.SECTION_VARIABLE && linkedSection })
    handleUnSelectBox();
    handleUnSelectContainer();
  };

  const uploadPdf = (file) => {
    if (!file) return
    const containerId = boxContainer?.id ?? selectedContainer?.id
    if (!containerId) return
    setTempContainerPdfToImage({ id: containerId, drawMode: drawPdfToImage, boxId: id, fileName: file.name })
    postPdfInTemplate({ id: documentId, boxId: id, file, setLoaderPdfToImage, currentDocumentType: documentType })
  }

  const handlePdfSelect = (event) => uploadPdf(event.target.files[0])

  // Document déjà stocké côté Costing : rien à téléverser, le backend en fait
  // une copie temporaire. Le reste du chemin (container cible, mise en page,
  // images reçues par websocket) est celui de l'import manuel.
  const injectCostingDocument = (document) => {
    const containerId = boxContainer?.id ?? selectedContainer?.id
    if (!containerId) return
    setTempContainerPdfToImage({ id: containerId, drawMode: drawPdfToImage, boxId: id, fileName: document.name })
    postCostingDocumentPdf({
      id: documentId,
      boxId: id,
      documentId: document.id,
      setLoaderPdfToImage,
      currentDocumentType: documentType,
    })
  }

  const iconElement = () => {
    if (box.type === ItemTypes.MASK) return <DuotoneLightIcon name='masks-theater' style={{ marginRight: "8px" }} />
    if (box.type === ItemTypes.SECTION_VARIABLE) return <DuotoneLightIcon name='question' style={{ marginRight: "8px" }} />
    return <DuotoneLightIcon name='rectangle-history-circle-plus' style={{ marginRight: "8px" }} />
  }

  const placeholder = useMemo(() => {
    if (box.type === ItemTypes.SECTION_VARIABLE) {
      if (documentType === 'template') return t("answer-only-proposal")
      return t("answer")
    }
    if (box.type === ItemTypes.RECURSIVE_SECTION) {
      return box.content?.childTemplateName ?? "Section enfant récursive"
    }
    return t(box.type)
  }, [box.type, box.content?.childTemplateName, documentType, t])


  // Bouton porté par l'emplacement vide : même action qu'un clic sur la box
  // (sélectionner, ce qui remplit l'inspecteur), plus le dépliage du panel s'il
  // était replié — sinon le clic n'aurait aucun effet visible.
  // Ordre imposé : `handleOverlayCollapse` repose la sélection de variable
  // (OverlayContext), ce qui remet `selectedBoxId` à null en modèle. Appelé
  // après `handleBoxSelect`, il annulait la sélection qu'on venait de faire.
  // Le panel déjà ouvert sur cette box, le clic ne changeait rien à l'écran :
  // la section où se fait le choix clignote pour dire où regarder.
  const renderOpenPanelButton = (label, autoOpenSelector = false) => (
    <Button
      size='small'
      type='primary'
      onClick={(event) => {
        event.stopPropagation();
        if (overlayProps?.right?.collapsed) handleOverlayCollapse('right', false);
        handleBoxSelect(event);
        setPanelFlash((nonce) => (nonce ?? 0) + 1);
        // Sélecteur déplié d'entrée : l'utilisateur voit les options tout de
        // suite, sans clic de plus dans un select fermé qu'il faudrait repérer.
        // Curseur posé dans la recherche : on tape le nom de la section sans
        // cliquer dans le champ.
        if (autoOpenSelector) {
          setSelectorOpen(true);
          setSelectorFocusNonce((nonce) => (nonce ?? 0) + 1);
        }
      }}
    >
      {label}
    </Button>
  );

  // Version historique, conservée pour le formulaire de variables : l'utilisateur
  // y remplit la box sur place, sans passer par l'inspecteur de droite. La mise
  // en page n'y est pas proposée : c'est celle choisie dans le modèle.
  const renderPdfSelector = () => <div style={{ display: "flex" }}>
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input style={{ width: 300, color: 'transparent' }} type='file' accept='application/pdf' onChange={handlePdfSelect} />
      <span style={{
        position: 'absolute',
        left: 140
      }}>{box.fileName ?? 'Aucun fichier choisi'}</span>
    </div>
  </div>

  // Mise en page AVANT la zone de dépôt : l'import démarre dès le fichier
  // déposé, en lisant ce choix. Le présenter après aurait laissé croire qu'on
  // peut encore le changer.
  const renderPdfPanel = () => <>
    {!landscape && (
      <BoxFormControlsPortal title={t('pdf_box_layout')} order={0}>
        <Radio.Group
          name="radiogroup"
          value={drawPdfToImage ? 1 : 2}
          onChange={(e) => setDrawPdfToImage(e?.target?.value === 1)}
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          <Radio value={1}>{t('pdf_box_with_header_footer')}</Radio>
          <Radio value={2}>{t('pdf_box_full_page')}</Radio>
        </Radio.Group>
      </BoxFormControlsPortal>
    )}
    <BoxFormControlsPortal
      title={t('pdf_box_file')}
      order={1}
      flashNonce={panelFlash}
      onFlashEnd={endPanelFlash}
    >
      {/* data-outside-ignore + preventDefault au mousedown : cliquer ou déposer
          ne déselectionne pas la box (cf. useOutsideAlerter), sinon le panel
          disparaîtrait sous le curseur. `image-dropzone` (Image.less) est la
          mise en forme partagée des zones de dépôt du panel. */}
      <div
        data-outside-ignore='true'
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <Upload.Dragger
          data-outside-ignore='true'
          accept='application/pdf'
          name='file'
          customRequest={({ file }) => uploadPdf(file)}
          showUploadList={false}
          className='image-dropzone'
        >
          <p style={{ margin: 0 }}>
            <DuotoneLightIcon name='file-pdf' weight='solid' style={{ fontSize: 26, color: accentColor }} />
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: PRIMARY_DARK }}>
            {t('drop-pdf')}
          </p>
        </Upload.Dragger>
      </div>
      {box.fileName && (
        <span style={{ fontSize: 11, color: PRIMARY_DARK, opacity: 0.7, wordBreak: 'break-all' }}>
          {box.fileName}
        </span>
      )}
    </BoxFormControlsPortal>
    {/* Documents déjà déposés dans Costing : même conversion que l'import
        manuel, sans re-téléverser le fichier. */}
    <BoxFormControlsPortal title={t('costing_documents')} order={2}>
      <CostingDocumentPicker onInject={injectCostingDocument} />
    </BoxFormControlsPortal>
  </>

  const isRecursiveBox = box.type === ItemTypes.RECURSIVE_SECTION;
  const hasInvalidChild = isRecursiveBox && selectionList.some((o) => o.disabled);
  const displayedSelectionList = isRecursiveBox && nestableOnly
    ? selectionList.filter((o) => !o.disabled)
    : selectionList;

  // In a proposal, offer a standard/recursive toggle (the template uses the sectionMode radio).
  const showRecursiveToggle =
    currentDocument.type !== 'template' &&
    box.type !== ItemTypes.MASK &&
    box.type !== ItemTypes.SECTION_VARIABLE &&
    !isRecursiveBox;

  const sectionFilterDropdownRender = (menu) => (
    <>
      <div style={{ padding: '6px 12px' }} onMouseDown={(e) => e.preventDefault()}>
        <Segmented
          block
          size="small"
          value={showRecursive ? 'recursive' : 'standard'}
          onChange={(v) => setShowRecursive(v === 'recursive')}
          options={[
            { label: t('add_section_standard'), value: 'standard' },
            { label: t('add_section_recursive'), value: 'recursive' },
          ]}
        />
      </div>
      {menu}
    </>
  );

  // "Imbricables uniquement" filter, shown only when there are invalid candidates.
  const recursiveDropdownRender = (menu) => (
    <>
      {hasInvalidChild && (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <Switch size="small" checked={nestableOnly} onChange={setNestableOnly} />
          <span style={{ fontSize: 12 }}>Imbricables uniquement</span>
        </div>
      )}
      {menu}
    </>
  );


  // Le mode d'insertion et le passage en question ne concernent que la
  // composition d'un modèle : dans un proposal, la section est injectée telle
  // quelle.
  const isSectionVariable = box.type === ItemTypes.SECTION_VARIABLE

  const showSectionModeControls =
    currentDocument.type === 'template' &&
    box.type !== ItemTypes.SECTION_VARIABLE &&
    box.type !== ItemTypes.MASK

  // Change la nature du bloc sur place : la box est remplacée à l'identique
  // d'emplacement, seul son type change.
  const replaceBoxWithType = (type, extra = {}) => {
    const newBox = initializeBoxContent({
      box: {
        type,
        ...gridConfiguration[type],
        copied: false,
        ...extra,
      },
      configuration,
      column,
      parentId: box.columnId
    });
    dropBoxInColumn({
      id: box.columnId,
      newBox,
      updateContainerHeight: true,
      drawMode: true,
      boxIdToReplace: id
    })
    // La box remplacée emporte la sélection avec elle : on la redonne à la
    // nouvelle, sinon changer de nature referme l'inspecteur et il faut
    // recliquer le bloc pour voir ce qu'on vient de choisir.
    selectBox({ box: newBox, fromViewer })
  }

  // Remplace cette box par une question « faut-il afficher cette section ? »,
  // à laquelle on répondra depuis le proposal.
  const convertToSectionVariable = () => replaceBoxWithType(ItemTypes.SECTION_VARIABLE)

  // Retour depuis la question de section vers un emplacement de section
  // ordinaire, dans le mode qu'on vient de cocher.
  const convertToSectionBox = (mode) =>
    replaceBoxWithType(ItemTypes.SECTION, { sectionMode: mode })

  // Masques et question de section (SECTION_VARIABLE, options = box.questions)
  // n'ont pas de dossier : seules les vraies sections (et sections récursives)
  // passent par le picker arborescent.
  const isFolderGroupedSection = box.type !== ItemTypes.MASK && box.type !== ItemTypes.SECTION_VARIABLE;

  const renderSelector = ({ width }) => {
    const commonProps = {
      width,
      onSelect: handleSelect,
      options: displayedSelectionList,
      placeholder,
      selectedValue,
      isOpen: selectorOpen,
      onToggleOpen: handleSelectorOpenChange,
      focusNonce: selectorFocusNonce,
      disabled: box.type === ItemTypes.SECTION_VARIABLE && documentType === 'template',
      // Empilement maîtrisé des deux côtés : antd calcule seul le z-index de
      // cette liste (et gagnait sur la modale d'aperçu, quelle que soit la
      // valeur qu'on lui donnait). On fixe donc les deux crans dans index.less
      // — liste en dessous, aperçu au-dessus — plutôt que de courir après le
      // nombre choisi par antd.
      popupClassName: 'section-selector-dropdown',
      popupMatchSelectWidth: false,
      // Liste plus large que le champ : les noms de section, souvent longs,
      // y étaient coupés.
      dropdownStyle: { width: 'min(640px, 90vw)' },
      dropdownRender: isRecursiveBox ? recursiveDropdownRender : (showRecursiveToggle ? sectionFilterDropdownRender : undefined),
    };
    if (isFolderGroupedSection) {
      return <SectionTreeSelector {...commonProps} suffixIcon={iconElement()} folders={folders} />;
    }
    return <NavbarSelector {...commonProps} tooltipContent={t(box.type)} suffixIcon={iconElement()} />;
  }

  // Version historique, conservée pour le formulaire de variables et pour la
  // question de section (SECTION_VARIABLE), à laquelle on répond dans le
  // document lui-même.
  const renderNavbarSelector = () => <div style={{ display: "flex" }
  }>
    {renderSelector({ width: (landscape ? pageHeight : pageWidth) / 2.2 })}
    {showSectionModeControls &&
      <div style={{ alignItems: "center", display: "flex", marginLeft: 10, flexShrink: 0 }}>
        <Radio.Group name="radiogroup" value={sectionMode} onChange={(e) => setSectionMode(e.target.value)} style={{ display: 'flex', flexWrap: 'nowrap', gap: 4 }}>
          <Radio value={1}>{t('section_mode_linked')}</Radio>
          <Radio value={2}>{t('section_mode_edition')}</Radio>
          <Radio value={3}>{t('section_mode_recursive')}</Radio>
        </Radio.Group>
        <BoxButton
          type={ItemTypes.SECTION_VARIABLE}
          text={t('section_variable')}
          icon={<DuotoneLightIcon name='rectangle-history-circle-plus' style={{ fontSize: iconFontSize }} />}
          bgcolor={VARIABLE_COLOR}
          disp='inline-block'
          color='#ffff'
          canDrag={false}
          marginBottom={0}
          marginLeft='auto'
          onClick={convertToSectionVariable}
        />
      </div>
    }
  </div>

  // Nature du bloc : les trois modes d'insertion et la question de section se
  // choisissent dans le même groupe, dans les deux sens — passer en « Variable »
  // remplace la box par une question, en ressortir la ramène à un emplacement de
  // section dans le mode coché.
  const renderSectionModePanel = () => (
    <BoxFormControlsPortal title={t('section_mode')} order={0}>
      <Radio.Group
        name="radiogroup"
        value={isSectionVariable ? SECTION_MODE_VARIABLE : sectionMode}
        onChange={(e) => {
          const mode = e.target.value;
          if (mode === SECTION_MODE_VARIABLE) return convertToSectionVariable();
          if (isSectionVariable) return convertToSectionBox(mode);
          return setSectionMode(mode);
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
      >
        <Radio value={1}>{t('section_mode_linked')}</Radio>
        <Radio value={2}>{t('section_mode_edition')}</Radio>
        <Radio value={3}>{t('section_mode_recursive')}</Radio>
        <Radio value={SECTION_MODE_VARIABLE}>{t('section_mode_variable')}</Radio>
      </Radio.Group>
    </BoxFormControlsPortal>
  )

  const renderSectionPanel = () => <>
    {/* Mode d'insertion AVANT le sélecteur de section : on choisit d'abord la
        nature (liée / édition / récursive), puis la section à poser. */}
    {showSectionModeControls && renderSectionModePanel()}
    <BoxFormControlsPortal
      title={t(box.type)}
      order={1}
      flashNonce={panelFlash}
      onFlashEnd={endPanelFlash}
      // Sélecteur déjà ouvert (autoOpenSelector) : le voile bleu ferait
      // doublon, on garde seulement le défilement jusqu'à la section.
      hideFlashOverlay
    >
      {renderSelector({ width: '100%' })}
    </BoxFormControlsPortal>
  </>

  return (
    <>
      <SectionPreviewModal
        sectionId={previewSectionId}
        open={previewSectionId != null}
        onClose={() => setPreviewSectionId(null)}
      />
      {pendingSplitChoice && (
        <Modal
          open={true}
          centered={true}
          onCancel={() => { if (!isApplyingSplit) setPendingSplitChoice(null); }}
          footer={[
            <Button key="cancel" onClick={() => { if (!isApplyingSplit) setPendingSplitChoice(null); }} disabled={isApplyingSplit}>
              {t('cancel')}
            </Button>,
            <Button key="non-split" type="primary" disabled={isApplyingSplit} onClick={async () => {
              const choice = pendingSplitChoice;
              setPendingSplitChoice(null);
              setIsApplyingSplit(true);
              try {
                await inject({ id: choice.sectionId, boxId: choice.boxId, linked_section: false, split: false });
                handleUnSelectBox();
                handleUnSelectContainer();
              } finally { setIsApplyingSplit(false); }
            }}>
              {t('table-split-modal-non-split')}
            </Button>,
            <Button key="split" type="primary" disabled={isApplyingSplit} onClick={async () => {
              const choice = pendingSplitChoice;
              setPendingSplitChoice(null);
              setIsApplyingSplit(true);
              try {
                await inject({ id: choice.sectionId, boxId: choice.boxId, linked_section: false, split: true });
                handleUnSelectBox();
                handleUnSelectContainer();
              } finally { setIsApplyingSplit(false); }
            }}>
              {t('table-split-modal-split')}
            </Button>,
          ]}
          title={null}
        >
          {t('table-split-modal-question')}
        </Modal>
      )}
      <div
        id={`box-${box?.id ? box.id : 'new'}`}
        onDoubleClick={(event) => {
          if (isAVariableBox(box?.type)) {
            updateLeftShownStatus(leftShownStatus["VARIABLES"], box.id)
          }
          event.stopPropagation()
        }}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: "100%",
          backgroundColor: "#bfbfbf",
          flexDirection: "column",
          rowGap: 8,
          // Emplacement encore vide : le clic a un effet visible (l'inspecteur
          // se remplit), le curseur l'annonce. Une fois la box sélectionnée il
          // ne révèle plus rien, on repasse en curseur normal.
          ...((isSectionSelector || isPdfBox) && !inFormViewer && !isSelected
            ? { cursor: 'pointer' }
            : {}),
        }}
        onClick={(event) => {
          handleBoxSelect(event)
        }}
      >
        {box.type === ItemTypes.SECTION_VARIABLE && <>
          <div>
            {isLoading ? <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} /> :
              <>
                {selectedSectionVariable && `Voulez-vous afficher la section ${selectedSectionVariable.name} ?`}
                {!selectedSectionVariable && box.sectionId && "La section à afficher n'est plus disponible"}
                {!selectedSectionVariable && !box.sectionId && box?.name?.getCurrentContent().getPlainText()}
              </>
            }
          </div>
          {selectedSectionVariable && renderNavbarSelector()}
        </>}
        {isSectionSelector && (inFormViewer
          ? renderNavbarSelector()
          : <PlaceholderCard
            iconName={box.type === ItemTypes.MASK ? 'masks-theater' : 'rectangle-history-circle-plus'}
            title={placeholder}
            hint={t('selection_box_hint')}
            action={renderOpenPanelButton(t('selection_box_action'), true)}
          />)}
        {isPdfBox && (inFormViewer
          ? renderPdfSelector()
          : <PlaceholderCard
            iconName='file-pdf'
            title={box.fileName ?? t('pdf_box_file')}
            hint={t('pdf_box_hint')}
            action={renderOpenPanelButton(t('pdf_box_action'))}
          />)}
        {/* Contrôles de composition : dans l'inspecteur, jamais en travers du
            document. Le formulaire de variables garde les siens sur place. */}
        {showBoxPanel && !inFormViewer && isPdfBox && renderPdfPanel()}
        {showBoxPanel && !inFormViewer && isSectionSelector && renderSectionPanel()}
        {/* Sans ça, cocher « Variable » était sans retour : la box changeait de
            type et le groupe de modes disparaissait avec l'ancien panneau. */}
        {showBoxPanel && !inFormViewer && isSectionVariable &&
          currentDocument.type === 'template' && renderSectionModePanel()}
        {showBoxPanel && isPdfBox && (
          <BoxFormControlsPortal title={t('display')} order={3}>
            <HideInFormSwitcher boxId={box.id} variant='panel' />
            <VariableSwitcher boxId={box.id} variant='panel' />
          </BoxFormControlsPortal>
        )}
        {box.type === ItemTypes.RECURSIVE_MODEL && <>
          <div style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 20px',
            boxSizing: 'border-box',
            gap: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <FontAwesomeIcon
                icon={icon({ name: 'layer-group', style: 'regular' })}
                style={{ fontSize: 18, color: '#1d1d1d', flexShrink: 0 }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: '#1d1d1d', fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                  {t('recursive_model_placeholder_title')}
                </div>
                <div style={{ color: '#434343', fontSize: 11, lineHeight: 1.4, marginTop: 2 }}>
                  {t('recursive_model_placeholder_sub')}
                </div>
              </div>
            </div>
            <Button
              icon={<FontAwesomeIcon icon={icon({ name: 'gear', style: 'regular' })} />}
              size="small"
              style={{ flexShrink: 0 }}
              onClick={(e) => { e.stopPropagation(); setRecursiveModelConfigOpen(true); }}
            >
              {t('recursive_model_config_button')}
            </Button>
          </div>
          <RecursiveModelConfigModal
            boxId={box.id}
            open={recursiveModelConfigOpen}
            onClose={() => setRecursiveModelConfigOpen(false)}
          />
        </>}
      </div>
    </>
  );
};

export default SelectionBox;
